import ReactGA from "react-ga4";

const maxArrayLength = 20;
const normalizePropertyKey = (key = "") =>
  String(key).toLowerCase().replace(/[^a-z0-9]/g, "");

const eventPiiKeys = new Set([
  "address",
  "billingaddress",
  "billingcity",
  "billingcompanyname",
  "billingzipcode",
  "code",
  "companyname",
  "couponcode",
  "email",
  "electronicinvoicingid",
  "firstname",
  "groupname",
  "lastname",
  "logincode",
  "magictoken",
  "mobile",
  "orgname",
  "otp",
  "pec",
  "personfullname",
  "phone",
  "sessiontoken",
  "token",
  "vat",
]);

const sanitizeValue = (value) => {
  if (value === undefined || typeof value === "function") {
    return undefined;
  }

  if (value === null) {
    return null;
  }

  if (Array.isArray(value)) {
    return value
      .slice(0, maxArrayLength)
      .map(sanitizeValue)
      .filter((item) => item !== undefined);
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === "object") {
    return sanitizeProperties(value);
  }

  return value;
};

export const sanitizeProperties = (props = {}) =>
  Object.entries(props).reduce((acc, [key, value]) => {
    const sanitizedValue = sanitizeValue(value);
    if (sanitizedValue !== undefined) {
      acc[key] = sanitizedValue;
    }
    return acc;
  }, {});

const stripEventPii = (value) => {
  if (Array.isArray(value)) {
    return value.map(stripEventPii);
  }

  if (value && typeof value === "object") {
    return Object.entries(value).reduce((acc, [key, childValue]) => {
      if (!eventPiiKeys.has(normalizePropertyKey(key))) {
        acc[key] = stripEventPii(childValue);
      }
      return acc;
    }, {});
  }

  return value;
};

const sanitizeEventProperties = (props = {}) =>
  stripEventPii(sanitizeProperties(props));

const callOpenPanel = (method, ...args) => {
  if (typeof window === "undefined" || !window.op) {
    return;
  }

  const fn = window.op[method];
  if (typeof fn !== "function") {
    return;
  }

  fn.apply(window.op, args);
};

export function trackEvent(name, props = {}) {
  const { label, category = "CardPage", ...rest } = sanitizeEventProperties(props);

  try {
    ReactGA.event({
      category,
      action: "Click",
      label: label || name,
    });
  } catch (err) {
    console.error("ReactGA trackEvent failed", err);
  }

  try {
    callOpenPanel("track", name, sanitizeProperties({ label, category, ...rest }));
  } catch (err) {
    console.error("OpenPanel trackEvent failed", err);
  }
}

export function trackFeatureEvent(name, props = {}) {
  trackEvent(name, {
    category: "Feature",
    ...props,
  });
}

export function identifyUser(profile = {}) {
  const sanitizedProfile = sanitizeProperties(profile);

  if (!sanitizedProfile.profileId) {
    return;
  }

  try {
    callOpenPanel("identify", sanitizedProfile);
  } catch (err) {
    console.error("OpenPanel identify failed", err);
  }
}

export function upsertGroup(group = {}) {
  const sanitizedGroup = sanitizeProperties(group);

  if (!sanitizedGroup.id) {
    return;
  }

  try {
    callOpenPanel("upsertGroup", {
      type: "company",
      ...sanitizedGroup,
    });
  } catch (err) {
    console.error("OpenPanel upsertGroup failed", err);
  }
}

export function setGroup(groupId) {
  if (!groupId) {
    return;
  }

  try {
    callOpenPanel("setGroup", groupId);
  } catch (err) {
    console.error("OpenPanel setGroup failed", err);
  }
}

export function clearIdentity() {
  try {
    callOpenPanel("clear");
  } catch (err) {
    console.error("OpenPanel clear failed", err);
  }
}
